import type {VideoStartFailedReason} from '../enums/VideoStartFailedReason';

import type {AnalyticsConfig} from './AnalyticsConfig';
import type {AnalyticsEventBase, CustomDataValuesEvent, ErrorEvent, QualityChangeEvent} from './EventData';
import type {Sample} from './Sample';
import type {SourceMetadata} from './SourceMetadata';

export type HeartbeatPayload = {
  buffered?: Sample['buffered'];
  /**
   * Flag indicating that the event was triggered by a program change.
   *
   * `true` if the event was triggered by a program change, `undefined` otherwise to not set it as a property in the sample.
   */
  isProgramChange?: true;
  played?: Sample['played'];
  /** Flag indicating that the event was triggered by SSAI */
  triggeredBySsai?: boolean;
};

export interface StateMachineCallbacks {
  setup: () => void;
  startup: (duration: number, state: string) => void;
  /** Should reset the analytics, update the source metadata, and send a "program change startup sample" */
  programChange: (sourceMetadata: SourceMetadata) => void;
  /** Should only update the source metadata in the analytics module, but without sending any sample. */
  updateSourceMetadata: (sourceMetadata: SourceMetadata) => void;
  playing: (duration: number, state: string) => void;
  heartbeat: (duration: number, state: 'buffering' | 'playing', payload: HeartbeatPayload) => void;
  /**
   * Should send a sample with state "qualitychange" and duration 0
   */
  qualitychange: (event: QualityChangeEvent) => void;
  /**
   * Should update the qualityInfo on the current sample but not send any sample, e.g when a qualityChange occurs during
   * pause, seeking or buffering or when the QUALITY_CHANGE_THRESHOLD was hit
   */
  updateSampleQualityInfo: (event: QualityChangeEvent) => void;
  audiotrack_changing: () => void;
  pause: (duration: number, state: string) => void;
  /**
   * Should send a sample with `state: "seeking"` and `duration` set to the duration of the seeking.
   */
  seeking: (duration: number) => void;
  buffering: (duration: number, state: string) => void;
  error: (event: Readonly<ErrorEvent>) => void;
  /**
   * @deprecated should not be used at all.
   *
   * Instead, the "end" player event should transition state machine to PAUSE state
   *
   * https://bitmovin.atlassian.net/browse/AN-5134
   */
  end: (duration: number, state: string) => void;
  closedAd: (duration: number) => void;
  closedPlaying: (duration: number) => void;
  ad: (duration: number, state: string) => void;
  mute: () => void;
  unMute: () => void;
  subtitle_changing: () => void;
  setVideoTimeEndFromEvent: (analyticsEventBase: AnalyticsEventBase) => void;
  setVideoTimeStartFromEvent: (analyticsEventBase: AnalyticsEventBase) => void;
  videoStartFailed: (duration: number, reason: VideoStartFailedReason) => void;
  startCasting: (duration: number, event: any) => void;
  casting: (duration: number, event: any) => void;
  /**
   * Should reset the whole adapter.
   *
   * @remarks
   *
   * {@link AnalyticsConfig} as event payload, is optional because this callback is called not just for player
   * source change and AdapterAPI source change, but also for player unload/stop, when the source should be just
   * unloaded.
   */
  manualSourceChange: (event: {config?: AnalyticsConfig}) => void;
  muting_ready: (duration: number, state: string, event: any) => void;
  /**
   * Should change {@link CustomDataValues} of analytics module.
   *
   * @remarks
   *
   * It is called via the default "onleavestate" state machine callback, and because of that:
   *    - it has to match the state name "CUSTOMDATACHANGE"
   *    - each parameter has to of `undefined` type (unintentionally, it can be called at any time, from any state)
   */
  customdatachange: (
    duration: number | undefined,
    state: string | undefined,
    eventData: CustomDataValuesEvent | undefined,
  ) => void;

  /** shuts down the collection and removes/clears all timeouts, intervals, event listeners */
  release: () => void;
}

/**
 * {@link StateMachineCallbacks} object with each callback set to be empty function (doing nothing).
 *
 * Why?:
 *  We need to set it as default value in {@link InternalAdapter} for use case, when {@link InternalAdapter}
 *  is already created and listen to player events, but {@link Analytics} was not instantiated/initialized yet (that
 *  should set correct {@link StateMachineCallbacks} implementation)
 */
export const defaultStateMachineCallbacks: StateMachineCallbacks = {
  setup: () => {},
  startup: () => {},
  programChange: () => {},
  updateSourceMetadata: () => {},
  playing: () => {},
  heartbeat: () => {},
  qualitychange: () => {},
  updateSampleQualityInfo: () => {},
  audiotrack_changing: () => {},
  pause: () => {},
  seeking: () => {},
  buffering: () => {},
  error: () => {},
  end: () => {},
  closedAd: () => {},
  closedPlaying: () => {},
  ad: () => {},
  mute: () => {},
  unMute: () => {},
  subtitle_changing: () => {},
  setVideoTimeEndFromEvent: () => {},
  setVideoTimeStartFromEvent: () => {},
  videoStartFailed: () => {},
  startCasting: () => {},
  casting: () => {},
  manualSourceChange: () => {},
  muting_ready: () => {},
  customdatachange: () => {},
  release: () => {},
};
